import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { orderApiService } from '../../../../../service/seller/orderApiService';
import { useLoader } from '../../../../../base/context/LoaderProvider';
import { ToastTypes, useMessage } from '../../../../../base/context/MessageProvider';
import Pagination from '../../../../library/pagination';
import ExcelTable from '../../../../library/excelTable';
import { FaCalendarAlt, FaFileExport, FaSpinner } from 'react-icons/fa';
import './style.css';

/**
 * Reusable Failed Orders Table Component
 * Used in both the Orders page (as a tab) and the dedicated Failed Orders page
 */
const FailedOrdersTable = ({ 
  // Optional: if you want to control state from parent
  externalFilters = null,
  showHeader = true,
  showFilters = false,
}) => {
  const EXPORT_BATCH_SIZE = 1000;
  const EXPORT_FETCH_CONCURRENCY = 5;
  const [failedOrders, setFailedOrders] = useState([]);
  const [failedOrdersCount, setFailedOrdersCount] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pages, setPages] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
  });
  
  const loader = useLoader();
  const message = useMessage();

  const [exportDateRange, setExportDateRange] = useState({ startDate: "", endDate: "" });
  const [exporting, setExporting] = useState(false);
  const [exportLoadingType, setExportLoadingType] = useState(null); // 'all' | 'date' | null
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch failed orders
  const fetchFailedOrders = async () => {
    const failedQueryParams = `?limit=${limit}&page=${page}`;
    
    setIsFetchingData(true);
    await loader.showLoading(true);
    orderApiService
      .getFailedOrders(failedQueryParams)
      .then(res => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          setFailedOrders(res.data.orders || []);
          setFailedOrdersCount(res.data.totalOrders || 0);
          setPages({
            currentPage: res.data.currentPage || 1,
            totalPages: res.data.totalPages || 0,
            totalItems: res.data.totalOrders || 0,
          });
        } else {
          setFailedOrders([]);
          setFailedOrdersCount(0);
        }
      })
      .catch(err => {
        loader.showLoading(false);
        setFailedOrders([]);
        setFailedOrdersCount(0);
      })
      .finally(() => {
        setIsFetchingData(false);
      });
  };

  useEffect(() => {
    fetchFailedOrders();
  }, [page, limit]);

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setLimit(newRowsPerPage);
    setPage(1); // Reset to first page when changing rows per page
  };

  // Flatten failed orders for ExcelTable (one row per orderDetail).
  // (Used for both table rendering and Excel export.)
  const flattenFailedOrders = (orders = []) =>
    (Array.isArray(orders) ? orders : []).flatMap((order) =>
      (order.orderDetails || []).map((detail, idx) => ({
        _id: `${order._id}-${idx}`,
        // India S.No. from originalExcelData
        indiaSNo:
          order.originalExcelData?.["India S.No"] ||
          order.originalExcelData?.["India S.No."] ||
          "-",
        orderId: order.orderId,
        orderItemId: detail.orderItemId || "-",
        sku: detail.sku || "-",
        ASIN: detail.ASIN || "-",
        productName: detail.productName || "-",
        productUrl: detail.productUrl || "-",
        quantity: detail.quantity || 1,
        consumerPrice: detail.consumerPrice || 0,
        // Order level fields
        orderType: order.orderType || "-",
        panel: order.panel || "-",
        // Use original date strings if available, otherwise format the date
        orderDateStr:
          order.orderDateStr || (order.orderDate ? moment(order.orderDate).format("DD-MMM") : "-"),
        shipByDateStr:
          order.shipByDateStr || (order.shipByDate ? moment(order.shipByDate).format("DD-MMM") : "-"),
        deliverByDateStr:
          order.deliverByDateStr ||
          (order.deliverByDate ? moment(order.deliverByDate).format("DD-MMM") : "-"),
        // Buyer info
        recipientName: order.recipientName || "-",
        shippingAddress1: order.shippingAddress1 || "-",
        shippingCity: order.shippingCity || "-",
        shippingState: order.shippingState || "-",
        shippingPostalCode: order.shippingPostalCode || "-",
        shippingPhoneNumber: order.shippingPhoneNumber || "-",
        createdAt: order.createdAt,
        status: "Failed",
        // Store full originalExcelData for reference
        originalExcelData: order.originalExcelData || {},
      }))
    );

  const failedOrdersFlattened = useMemo(() => flattenFailedOrders(failedOrders), [failedOrders]);

  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    if (stringValue.includes('"') || stringValue.includes(",") || stringValue.includes("\n")) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const exportRowsToCsv = (rows, columns, filePrefix) => {
    const headers = columns.map((col) => escapeCsvValue(col.label)).join(",");
    const csvRows = rows.map((row) =>
      columns
        .map((col) => {
          const value = row[col.key];
          return escapeCsvValue(value);
        })
        .join(",")
    );

    const csvContent = [headers, ...csvRows].join("\n");
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const finalFilename = `${filePrefix}-${timestamp}.csv`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", finalFilename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return { success: true, filename: finalFilename };
  };

  const fetchAllFailedOrdersForExport = async ({ startDate = "", endDate = "" } = {}) => {
    const baseParams = new URLSearchParams();
    baseParams.set("limit", String(EXPORT_BATCH_SIZE));
    if (startDate) baseParams.set("startDate", startDate);
    if (endDate) baseParams.set("endDate", endDate);

    const fetchPage = async (pageNumber) => {
      const params = new URLSearchParams(baseParams.toString());
      params.set("page", String(pageNumber));
      const response = await orderApiService.getFailedOrders(`?${params.toString()}`);
      if (response?.status?.code !== 200) {
        throw new Error(response?.error?.message || "Failed to fetch failed orders for export");
      }
      return {
        orders: response?.data?.orders || [],
        totalPages: Number(response?.data?.totalPages || 1),
      };
    };

    const firstPage = await fetchPage(1);
    const allOrders = [...firstPage.orders];
    const totalPages = firstPage.totalPages;

    if (totalPages <= 1) return allOrders;

    const remainingPages = [];
    for (let pageNumber = 2; pageNumber <= totalPages; pageNumber += 1) {
      remainingPages.push(pageNumber);
    }

    for (let i = 0; i < remainingPages.length; i += EXPORT_FETCH_CONCURRENCY) {
      const pageChunk = remainingPages.slice(i, i + EXPORT_FETCH_CONCURRENCY);
      const chunkResults = await Promise.all(pageChunk.map((pageNumber) => fetchPage(pageNumber)));
      chunkResults.forEach((result) => {
        allOrders.push(...result.orders);
      });
    }

    return allOrders;
  };

  // Failed orders table columns - showing all Excel data
  const failedOrderColumns = [
    {
      key: 'indiaSNo',
      label: 'India S.No.',
      width: 90,
      render: (value) => (
        <span style={{ 
          fontWeight: 600, 
          color: value && value !== '-' ? '#7c3aed' : '#9ca3af',
          backgroundColor: value && value !== '-' ? '#f3e8ff' : 'transparent',
          padding: value && value !== '-' ? '2px 6px' : '0',
          borderRadius: '4px'
        }}>
          {value || '-'}
        </span>
      ),
    },
    {
      key: 'orderType',
      label: 'Order Type',
      width: 100,
    },
    {
      key: 'orderItemId',
      label: 'Order Item ID',
      width: 150,
    },
    {
      key: 'panel',
      label: 'Panel',
      width: 80,
    },
    {
      key: 'orderDateStr',
      label: 'Order Date',
      width: 100,
    },
    {
      key: 'orderId',
      label: 'Order ID',
      width: 180,
      render: (value) => (
        <span style={{ color: '#3b82f6', fontWeight: 500 }}>{value}</span>
      )
    },
    {
      key: 'productName',
      label: 'Product Name',
      width: 250,
      render: (value) => (
        <span style={{ 
          maxWidth: '250px', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          display: 'block'
        }} title={value}>
          {value}
        </span>
      )
    },
    {
      key: 'sku',
      label: 'SKU',
      width: 180,
      render: (value) => (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{value}</span>
      )
    },
    {
      key: 'ASIN',
      label: 'ASIN',
      width: 120,
      render: (value) => (
        value && value !== '-' ? (
          <span 
            style={{ 
              background: '#fef3c7', 
              color: '#92400e', 
              padding: '2px 8px', 
              borderRadius: '4px', 
              fontSize: '12px', 
              fontWeight: 500,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
            onClick={(e) => {
              e.stopPropagation();
              // Open Amazon product scrape page
              window.open(`/seller/amazon-product?asin=${value}`, '_blank');
            }}
            title={`Scrape ${value} from Amazon`}
          >
            {value}
            <span style={{ fontSize: '10px' }}>🔍</span>
          </span>
        ) : '-'
      )
    },
    {
      key: 'productUrl',
      label: 'URL',
      width: 80,
      render: (value) => (
        value && value !== '-' ? (
          <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
            Link
          </a>
        ) : '-'
      )
    },
    {
      key: 'quantity',
      label: 'Qty',
      width: 50,
    },
    {
      key: 'consumerPrice',
      label: 'Order Value',
      width: 100,
      render: (value) => (
        <span>₹{value}</span>
      )
    },
    {
      key: 'shipByDateStr',
      label: 'Ship By Date',
      width: 100,
    },
    {
      key: 'deliverByDateStr',
      label: 'Deliver By Date',
      width: 110,
    },
    {
      key: 'recipientName',
      label: 'Buyer Name',
      width: 150,
    },
    {
      key: 'shippingAddress1',
      label: 'Buyer Address',
      width: 200,
      render: (value) => (
        <span style={{ 
          maxWidth: '200px', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          display: 'block'
        }} title={value}>
          {value}
        </span>
      )
    },
    {
      key: 'shippingCity',
      label: 'City',
      width: 100,
    },
    {
      key: 'shippingState',
      label: 'State',
      width: 120,
    },
    {
      key: 'shippingPostalCode',
      label: 'Pincode',
      width: 80,
    },
    {
      key: 'shippingPhoneNumber',
      label: 'Mob. No.',
      width: 110,
    },
    {
      key: 'status',
      label: 'Status',
      width: 80,
      render: () => (
        <span style={{ 
          background: '#fee2e2', 
          color: '#dc2626', 
          padding: '4px 8px', 
          borderRadius: '4px', 
          fontSize: '11px', 
          fontWeight: 500 
        }}>
          Failed
        </span>
      )
    },
  ];

  const handleExportAllFailedOrders = async () => {
    if (exporting) return;
    setExporting(true);
    setExportLoadingType("all");
    loader.showLoading(true);

    try {
      const orders = await fetchAllFailedOrdersForExport();
      const exportRows = flattenFailedOrders(orders);

      if (exportRows.length === 0) {
        message.showToast("No failed orders found to export", ToastTypes.WARNING);
        return;
      }

      const result = exportRowsToCsv(exportRows, failedOrderColumns, "failed-orders-all");

      if (!result?.success) {
        throw new Error(result?.error || "Failed to export failed orders");
      }

      message.showToast(`Exported ${exportRows.length} failed order rows successfully`, ToastTypes.SUCCESS);
    } catch (err) {
      message.showToast(err?.message || "Error exporting failed orders", ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
      setExporting(false);
      setExportLoadingType(null);
    }
  };

  const handleExportDateWise = async () => {
    if (exporting) return;
    if (!exportDateRange.startDate || !exportDateRange.endDate) {
      message.showToast("Please select start date and end date", ToastTypes.WARNING);
      return;
    }

    setExporting(true);
    setExportLoadingType("date");
    loader.showLoading(true);

    try {
      const orders = await fetchAllFailedOrdersForExport({
        startDate: exportDateRange.startDate,
        endDate: exportDateRange.endDate,
      });
      const exportRows = flattenFailedOrders(orders);

      if (exportRows.length === 0) {
        message.showToast("No failed orders found for selected date range", ToastTypes.WARNING);
        return;
      }

      const dateStr = `${exportDateRange.startDate}_to_${exportDateRange.endDate}`;
      const result = exportRowsToCsv(exportRows, failedOrderColumns, `failed-orders-${dateStr}`);

      if (!result?.success) {
        throw new Error(result?.error || "Failed to export failed orders (date wise)");
      }

      message.showToast(
        `Exported ${exportRows.length} failed order rows successfully (${dateStr})`,
        ToastTypes.SUCCESS
      );
    } catch (err) {
      message.showToast(err?.message || "Error exporting failed orders (date wise)", ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
      setExporting(false);
      setExportLoadingType(null);
    }
  };

  // Custom export dropdown like User Management:
  // - Export Date Wise (date range)
  // - Export All Orders
  const customExportOptions = useMemo(() => {
    return [
      {
        type: "date-range",
        dateRangeComponent: (
          <div style={{ padding: "8px", borderBottom: "1px solid #e5e7eb" }}>
            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "12px",
                fontWeight: "600",
                color: "#24292f",
              }}
            >
              Date Range Export
            </label>
            <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
              <input
                type="date"
                value={exportDateRange.startDate}
                onChange={(e) => setExportDateRange({ ...exportDateRange, startDate: e.target.value })}
                style={{
                  flex: 1,
                  padding: "6px",
                  fontSize: "12px",
                  border: "1px solid #d0d7de",
                  borderRadius: "4px",
                }}
              />
              <input
                type="date"
                value={exportDateRange.endDate}
                onChange={(e) => setExportDateRange({ ...exportDateRange, endDate: e.target.value })}
                style={{
                  flex: 1,
                  padding: "6px",
                  fontSize: "12px",
                  border: "1px solid #d0d7de",
                  borderRadius: "4px",
                }}
              />
            </div>
            <button
              onClick={handleExportDateWise}
              disabled={exporting || !exportDateRange.startDate || !exportDateRange.endDate}
              style={{
                width: "100%",
                padding: "6px 8px",
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                height: "32px",
              }}
            >
              {exportLoadingType === "date" ? (
                <>
                  <FaSpinner className="failed-export-spinner" /> Loading...
                </>
              ) : (
                <>
                  <FaCalendarAlt /> Export Date Wise
                </>
              )}
            </button>
          </div>
        ),
      },
      {
        type: "divider",
      },
      {
        type: "custom",
        component: (
          <div style={{ padding: "8px" }}>
            <button
              onClick={handleExportAllFailedOrders}
              disabled={exporting}
              style={{
                width: "100%",
                padding: "6px 8px",
                backgroundColor: "#2196F3",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                height: "32px",
              }}
            >
              {exportLoadingType === "all" ? (
                <>
                  <FaSpinner className="failed-export-spinner" /> Loading...
                </>
              ) : (
                <>
                  <FaFileExport /> Export All Orders
                </>
              )}
            </button>
          </div>
        ),
      },
    ];
  }, [exportDateRange, exporting, failedOrderColumns]); // eslint-disable-line react-hooks/exhaustive-deps
  
  return (
    <div className="failed-orders-table-container">
      {showHeader && (
        <div className="failed-orders-header">
          <h2>Failed Orders</h2>
          <div className="failed-orders-header-controls">
            <div className="failed-orders-count">
              Total: <strong>{failedOrdersCount}</strong>
            </div>
            <div className="rows-per-page-selector">
              <label htmlFor="rows-per-page">Rows per page:</label>
              <select 
                id="rows-per-page"
                value={rowsPerPage} 
                onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                className="rows-per-page-select"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
          </div>
        </div>
      )}
      
      <ExcelTable
        data={failedOrdersFlattened}
        columns={failedOrderColumns}
        sortable={true}
        resizable={true}
        selectable={false}
        showToolbar={true}
        title="Failed Orders - SKU/ASIN Not Found"
        showToolbarFilter={showFilters}
        customExportOptions={customExportOptions}
      />
      
      {failedOrders.length > 0 && (
        <div className="failed-orders-pagination">
          <Pagination pages={pages} page={page} setPage={setPage} />
        </div>
      )}
    </div>
  );
};

export default FailedOrdersTable;
